<template>
  <v-app>
    <v-app-bar theme="dark" data-cy="appBar" :height="appBarHeight">
      <FusionAppBar
        :logoSrc="logoSrc"
        :logoWidth="logoWidth"
        :isLoading="isLoading"
        :hasDrawer="hasDrawer"
        @drawerClicked="drawerToggle"
        @navigateHome="navigateHome"
      >
        <template v-slot:burger>
          <span :class="{ 'application-burger': hasDrawer && lgAndDown }">
            <v-app-bar-nav-icon
              v-if="hasDrawer && lgAndDown"
              @click.stop="drawerToggle"
              class="mx-2"
              size="x-large"
              density="comfortable"
              :color="theme.current.value.colors.textPrimary"
              data-cy="hamburgerButton"
            />
          </span>
        </template>
        <template v-slot:right-toolbar>
          <GlobalNotifications v-if="isAuthenticated" theme="light" />
          <FusionAuthenticator
            :userProfileTabItems="userProfileTabItems"
            :subscriptionDetailsConfig="{ showManageSubscriptionsButton: true }"
            @navigateHome="navigateHome"
          >
            <template v-slot:subscription-list>
              <AccountSubscriptions />
            </template>
          </FusionAuthenticator>
        </template>
      </FusionAppBar>
    </v-app-bar>
    <v-navigation-drawer
      v-if="hasDrawer"
      v-model="drawer.open"
      :permanent="drawerPermanent"
      :order="drawerOrder"
      :rail="drawerMini"
      rail-width="68"
      width="240"
      color="#f2f2f2"
    >
      <FusionSideBar :routes="routes" :drawerMini="drawerMini" data-cy="appSideBar"></FusionSideBar>
      <template v-if="drawer.open" v-slot:append>
        <v-list-item @click.stop="openFeedback" data-cy="feedbackIcon">
          <template v-slot:prepend>
            <v-icon>mdi-bullhorn</v-icon>
          </template>
          <v-list-item-title v-if="!drawerMini">
            {{ $t('app.feedback.feedback') }}
          </v-list-item-title>
        </v-list-item>
        <v-divider />
        <span class="footer" v-if="!drawerMini">
          <span class="footer--logo">
            &copy; {{ new Date().getFullYear() }}
            {{ $t('app.copyright') }}
          </span>
          <span class="footer--version">v{{ $store.state.releaseVersion }}</span>
        </span>
      </template>
    </v-navigation-drawer>
    <FusionFeedbackDialog
      :open="dialogs.feedback"
      :helpRoute="'/help'"
      @close="dialogs.feedback = false"
    />
    <v-main>
      <v-container class="main-content" fluid>
        <router-view />
        <FusionBannerMessage />
        <MovedToAnotherDeviceDialog />
        <DeviceConnectedAfterStartIncompleteDataDialog />
      </v-container>
    </v-main>
    <FusionSnackbar />
    <FusionFeatureFlags v-if="authUserData" :user="authUserData" />
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify'
import { FusionMixins, FusionStores } from '@fusion/saas-components-v3'
import { getAuthInstance, type Auth0User } from '@fusion/auth-v3'
import GlobalNotifications from '@/components/alerts/GlobalNotifications.vue'
import MovedToAnotherDeviceDialog from '@/components/dialogs/MovedToAnotherDeviceDialog.vue'
import DeviceConnectedAfterStartIncompleteDataDialog from '@/components/dialogs/DeviceConnectedAfterStartIncompleteDataDialog.vue'
import AccountSubscriptions from '@/components/userProfile/accountSubscriptions/AccountSubscriptions.vue'
import { getModelsMetaData } from '@/services/device-service'
import { DEVICE_UPDATE_INTERVAL_MS } from '@/services/device-metadata'
import { checkStatus } from '@/api/services/utils'
import { NAMESPACES } from '@/store'
import { SUBSCRIPTIONS_ACTIONS } from '@/store/subscriptions'
import { DEVICE_INTERVALS_ACTIONS } from '@/store/device-intervals'
import { DEVICE_ACTIONS } from '@/store/devices'
import { DEVICE_MODELS_ACTIONS } from '@/store/devicemodels'
import { USER_ACTIONS } from '@/store/user'

const TSI_HOME_LINK = 'https://tsi.com/home/'

export default defineComponent({
  components: {
    GlobalNotifications,
    MovedToAnotherDeviceDialog,
    DeviceConnectedAfterStartIncompleteDataDialog,
    AccountSubscriptions
  },
  setup() {
    const router = useRouter()
    const { drawer, drawerPermanent, drawerMini, drawerToggle } = FusionMixins.drawer()
    const { navigateTo, navigateBack } = FusionMixins.navigation(router)
    const loginas = FusionStores.loginas()
    const { lgAndDown, mdAndUp, lg, md } = useDisplay()
    const theme = useTheme()
    const userProfile = FusionStores.userProfile(getAuthInstance())

    return {
      lgAndDown,
      mdAndUp,
      lg,
      md,
      theme,
      drawer,
      drawerPermanent,
      drawerMini,
      drawerToggle,
      navigateTo,
      navigateBack,
      loginas,
      userProfile
    }
  },
  created() {
    document.title = this.$t('app.documentTitle')
  },
  async mounted() {
    if (this.isAuthenticated) {
      this.startDataUpdateInterval()
      this.initializePendo()
      await this.updatePermissions()
      await this.loadUserProfileSettings()
    }
  },
  data() {
    return {
      userProfileTabItems: [
        {
          title: this.$t('userProfile.unitSettings.title'),
          componentName: 'FusionUnitSettings'
        }
      ],
      dialogs: {
        feedback: false
      },
      noteNum: 7,
      mini: true,
      logoSrc: '/img/tsi_link_logo.svg',
      progress: {
        opacity: 0.3,
        bufferVal: 100,
        height: 4
      }
    }
  },
  watch: {
    async isAuthenticated() {
      if (this.isAuthenticated) {
        this.startDataUpdateInterval()
        this.initializePendo()
        await this.updatePermissions()
        await this.loadUserProfileSettings()
      } else {
        this.stopDevicesInterval()
      }
    },
    inMapMovingMode(isMoving: boolean) {
      if (isMoving) {
        this.stopDevicesInterval()
      } else {
        this.startDataUpdateInterval()
      }
    },
    currentRouteName(currentRouteName: string) {
      if (currentRouteName && this.isAuthenticated) {
        // Refresh movedToAnotherDeviceMessage every time the user goes to a different view.
        this.setUserSettings()
      }
    }
  },
  computed: {
    appVersion() {
      return APP_VERSION
    },
    appBarHeight() {
      return this.mdAndUp ? 64 : 56
    },
    logoWidth() {
      const width = (206 / 64) * this.appBarHeight
      return String(width)
    },
    hasDrawer() {
      return this.$auth.isAuthenticated
    },
    drawerOrder() {
      return this.drawerPermanent ? 0 : -1
    },
    routes() {
      return this.$router.options.routes
    },
    isAuthenticated() {
      return this.$auth.isAuthenticated
    },
    isLoading() {
      return this.$auth.loading
    },
    authUserData(): Auth0User | undefined {
      if (this.$auth?.user && this.$auth.user['https://tsi.com/user_metadata']?.account_id) {
        return this.$auth.user as Auth0User
      }
      return undefined
    },
    inMapMovingMode() {
      return this.$store.state.map.movingMode
    },
    isLoggedInAs() {
      return this.loginas.getIsLogInAs
    },
    currentRouteName() {
      return this.$route?.name || ''
    }
  },
  methods: {
    openFeedback(): void {
      this.dialogs.feedback = true
    },
    navigateHome(): void {
      window.open(TSI_HOME_LINK, '_blank', 'noopener,noreferrer')
    },
    initializePendo(): void {
      const user = this.authUserData
      const userMetadata = user?.['https://tsi.com/user_metadata']
      if (userMetadata && userMetadata.account_id) {
        pendo?.initialize({
          visitor: {
            id: userMetadata?.account_id,
            email: user?.email,
            firstName: user?.name,
            lastName: userMetadata?.family_name,
            sapAccountId: userMetadata?.sap_account_id
          },
          account: {
            id: userMetadata?.account_id
          }
        })
      }
    },
    updateSubscriptions(): void {
      this.$store.dispatch(
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS}`,
        {
          auth: this.$auth,
          api: this.$api
        }
      )
    },
    startDataUpdateInterval(): void {
      this.getModelsMeta()
      // Updates data (devices, subscriptions, telemetry, and user settings) in Vuex every 60 seconds
      // using the Vuex deviceIntervals module
      this.$store.dispatch(
        `${NAMESPACES.DEVICE_INTERVALS}/${DEVICE_INTERVALS_ACTIONS.START_APP_INTERVAL}`,
        {
          callback: this.updateUserData,
          interval: DEVICE_UPDATE_INTERVAL_MS
        }
      )
    },
    stopDevicesInterval(): void {
      this.$store.dispatch(
        `${NAMESPACES.DEVICE_INTERVALS}/${DEVICE_INTERVALS_ACTIONS.CLEAR_INTERVAL}`
      )
      this.updateSubscriptions()
    },
    async getUserDevices(): Promise<void> {
      await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_DEVICES}`)
      this.updateSubscriptions()
    },
    async getModelsMeta(): Promise<void> {
      const modelsData = await getModelsMetaData()
      this.$store.dispatch(
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_ACTIONS.SET_MODELS_META_DATA}`,
        modelsData
      )
    },
    async setUserSettings(): Promise<void> {
      try {
        const resp = await this.$api.getUserSettings()
        if (checkStatus(resp)) {
          const body = await resp.json()
          await this.$store.dispatch(`${NAMESPACES.USER}/${USER_ACTIONS.SET_SETTINGS}`, body)
        } else {
          await this.$store.dispatch(`${NAMESPACES.USER}/${USER_ACTIONS.SET_SETTINGS}`, {
            bluesky: { visited: false }
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching user settings:', error)
        // do not show user settings
      }
    },
    async updatePermissions(): Promise<void> {
      if (this.isLoggedInAs) {
        await this.loginas.updateImpersonatedUserPermissions(APP_ENV.VITE_APP_SUBS_HOST, this.$auth)
      }
    },
    async loadUserProfileSettings(): Promise<void> {
      try {
        await this.userProfile.getUserSettings(APP_ENV.VITE_APP_ACCOUNTS_HOST)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading user profile settings:', error)
      }
    },
    updateUserData(): void {
      this.getUserDevices()
      this.setUserSettings()
    }
  }
})
</script>

<style lang="scss">
@use '@/assets/global-variables' as *;

main {
  background-image: $background-gradient;
  background-repeat: repeat;
}

.authenticator--wrapper .v-btn[data-cy='loginButton'] {
  display: none;
}

.application-burger {
  background-color: #fff;
  height: 100%;
  flex: inherit;
  display: flex;
  align-items: center;
}

.v-navigation-drawer__append {
  .v-list-item {
    height: 60px;
  }

  .v-list-item__prepend {
    padding-left: 6px;
  }

  .footer {
    opacity: 0.3;
    padding: 0.4rem;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    max-height: 74px;
    white-space: nowrap;
  }
}

.v-badge__badge {
  height: unset !important;
  min-width: unset !important;
}

.v-alert {
  position: absolute;
  z-index: 100;
  top: 0;
  margin: 24px;
}

.main-content {
  position: relative;
  min-height: calc(100dvh - var(--v-layout-top));
  padding: 0.1px;
}
</style>
